import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { VariableFinancieraService } from './services/variable-financiera.service';
import { IVariableFinanciera } from './interfaces/variable-financiera.interface';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-variable-financiera-form',
  templateUrl: './variable-financiera-form.component.html',
  providers: [MessageService]
})
export class VariableFinancieraFormComponent implements OnInit, OnChanges {
  @Input() variable: IVariableFinanciera | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();

  form!: FormGroup;
  loading: boolean = false;
  opcionesTipoRelacion: { sOpcion: string; sValor: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private variableFinancieraService: VariableFinancieraService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.cargarOpcionesTipoRelacion();
    if (this.variable) {
      this.loadVariable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['variable'] && changes['variable'].currentValue) {
      this.loadVariable();
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      numero_relacion: ['', [Validators.required]],
      tipo: ['', [Validators.required]],
      descripcion: ['', [Validators.required]],
      formula: ['', [Validators.required]]
    });
  }

  cargarOpcionesTipoRelacion(): void {
    this.variableFinancieraService.getOpcionesTipoRelacion().subscribe({
      next: (resp) => {
        if (resp.success) {
          this.opcionesTipoRelacion = resp.data;
        }
      },
      error: (err) => {
        this.opcionesTipoRelacion = [];
      }
    });
  }

  loadVariable(): void {
    if (this.variable && this.variable.numero_relacion) {
      this.loading = true;
      this.variableFinancieraService.getVariableFinancieraForEdit(this.variable.numero_relacion).subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const variable = response.data[0];
            this.form.patchValue({
              numero_relacion: variable.numero_relacion,
              tipo: variable.tipo,
              descripcion: variable.descripcion,
              formula: variable.formula || ''
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar la variable financiera'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar la variable financiera'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else if (this.variable) {
      this.form.patchValue({
        numero_relacion: this.variable.numero_relacion,
        tipo: this.variable.tipo,
        descripcion: this.variable.descripcion,
        formula: this.variable.formula || ''
      });
    }
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload = {
        que: 1,
        tope: '',
        cmd: '',
        numero_relacion: formValue.numero_relacion,
        tipo: formValue.tipo,
        descripcion: formValue.descripcion,
        formula: formValue.formula,
        periodicidad: '',
        tipoVariable: ''
      };
      this.variableFinancieraService.saveVariableFinanciera(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({ success: true, message: 'Variable financiera creada correctamente' });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.data?.error || 'Error al crear la variable financiera'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la variable financiera'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  update(): void {
    if (this.form.valid && this.variable) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload = {
        que: 2,
        tope: '',
        cmd: '',
        numero_relacion: formValue.numero_relacion,
        tipo: formValue.tipo,
        descripcion: formValue.descripcion,
        formula: formValue.formula,
        periodicidad: '',
        tipoVariable: ''
      };
      this.variableFinancieraService.updateVariableFinanciera(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({ success: true, message: 'Variable financiera actualizada correctamente' });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.data?.error || 'Error al actualizar la variable financiera'
            });
          }
        },  
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la variable financiera'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'catalogovariablesfinancieras',
        sClave: this.variable?.numero_relacion?.toString() || '',
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  clearForm(): void {
    this.initForm();
  }
} 